import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { MaskLine } from "./Motion";
import { HeroVisual } from "./HeroVisual";
import { WhatsAppIcon } from "./Brand";
import { scrollToId, EASE } from "../../lib/site";
import { useQuiz } from "../../hooks/QuizContext";

const Curves = () => (
  <svg className="pointer-events-none absolute inset-0 h-full w-full" viewBox="0 0 1440 900" preserveAspectRatio="none" aria-hidden="true">
    {["M-40 640 C 320 520, 620 860, 1480 600", "M-40 180 C 380 40, 900 260, 1480 60"].map((d, i) => (
      <motion.path
        key={d}
        d={d}
        fill="none"
        stroke="url(#rg)"
        strokeWidth="1.4"
        initial={{ pathLength: 0, opacity: 0 }}
        animate={{ pathLength: 1, opacity: 0.85 }}
        transition={{ duration: 2.6, ease: EASE, delay: 0.4 + i * 0.4 }}
      />
    ))}
    <defs>
      <linearGradient id="rg" x1="0" x2="1">
        <stop offset="0" stopColor="#C87A63" stopOpacity="0" />
        <stop offset="0.5" stopColor="#B8654F" />
        <stop offset="1" stopColor="#E4A08B" stopOpacity="0.2" />
      </linearGradient>
    </defs>
  </svg>
);

const HeroTitle = () => (
  <h1 className="hero-title" data-testid="hero-title">
    <span className="sr-only">Laser Day — depilação a laser na Aisla Studio</span>
    <span aria-hidden="true">
      <MaskLine delay={0.35} className="text-choco pr-[0.08em]">LASER</MaskLine>
      <MaskLine delay={0.5} className="text-metal pl-[0.42em] lg:pl-[0.6em]">DAY</MaskLine>
    </span>
  </h1>
);

const HeroCta = () => {
  const { openQuiz } = useQuiz();
  return (
    <button
      type="button"
      onClick={() => openQuiz("hero")}
      className="btn-rose"
      data-testid="hero-cta-whatsapp"
    >
      <WhatsAppIcon className="h-5 w-5" /> Agende sua sessão
    </button>
  );
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-18%"]);
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "12%"]);

  return (
    <section id="topo" ref={ref} className="relative overflow-hidden pt-[96px]" aria-labelledby="hero-heading" data-testid="hero-section">
      <motion.div style={{ y: bgY }} className="absolute inset-0 -z-10" aria-hidden="true">
        <img src="/img/satin.webp" alt="" className="h-full w-full scale-110 object-cover opacity-70" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#FAF5EE]/70 via-[#FAF5EE]/55 to-[#FAF5EE]" />
      </motion.div>
      <Curves />
      <div className="relative mx-auto grid max-w-[1400px] items-center gap-12 px-5 pb-20 sm:px-8 lg:grid-cols-12 lg:gap-6 lg:px-12 lg:pb-28">
        <motion.div style={{ y: textY }} className="lg:col-span-7">
          <span id="hero-heading" className="sr-only">Laser Day</span>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 1 }} className="eyebrow mb-4 text-[#9A4A38]">
            Aisla Studio apresenta
          </motion.p>
          <HeroTitle />
          <MaskLine delay={0.75} className="mt-3 text-[0.78rem] font-semibold tracking-[0.62em] text-[#2D1C18] sm:text-base sm:tracking-[0.8em]">
            DEPILAÇÃO A LASER
          </MaskLine>
          <div className="mt-10 flex flex-col gap-8 sm:flex-row sm:items-end sm:gap-12">
            <div>
              <MaskLine delay={0.9} className="font-script pt-3 pl-1 text-6xl leading-[1.1] text-[#8E3F30] sm:text-7xl">Pele lisa,</MaskLine>
              <MaskLine delay={1.0} className="mt-1 whitespace-nowrap text-sm font-semibold tracking-[0.28em] text-[#6B3A2E]">MAIS CONFIANÇA TODO DIA</MaskLine>
            </div>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.15, duration: 0.9, ease: EASE }} className="flex flex-wrap gap-3">
              <HeroCta />
              <button type="button" onClick={() => scrollToId("oferta")} className="btn-ghost" data-testid="hero-see-offer-btn">
                Ver oferta <ArrowDown className="h-4 w-4" aria-hidden="true" />
              </button>
            </motion.div>
          </div>
        </motion.div>
        <div className="lg:col-span-5">
          <HeroVisual />
        </div>
      </div>
    </section>
  );
};
